import { NgClass, NgTemplateOutlet, DatePipe, DecimalPipe, TitleCasePipe } from "@angular/common";
import { Component, InputSignal, input, inject, WritableSignal, signal, computed, Signal, effect } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import QRCode from "qrcode";
import { InvoiceDto } from "../../../../../../../api/models/fraud.models";
import { CopyToClipboardCopiedDirective } from "../../../../../../directives/copy-to-clipboard-copied.directive";
import { getPriceRange, groupCostProviders } from "../../../../../../utils/pricing.utils";
import { AuthService } from "../../../../../../services/auth.service";
import { ApiService } from "../../../../../../../api/services/api.service";
import { CropWithDotsCenterPipe } from "../../../../../../pipes/crop-with-dots-center.pipe";
import { ProjectSettingsService } from "../../../../../../services/project-settings.service";

@Component({
  selector: 'app-plan-upgrade-awaiting-payment',
  imports: [
    NgClass,
    MatIcon,
    CopyToClipboardCopiedDirective,
    NgTemplateOutlet,
    DatePipe,
    DecimalPipe,
    TitleCasePipe,
    CropWithDotsCenterPipe,
  ],
  templateUrl: './plan-upgrade-awaiting-payment.component.html',
  styleUrl: './plan-upgrade-awaiting-payment.component.scss',
  host: {
    class: 'flex flex-col w-full h-full min-h-0 overflow-y-auto md:overflow-hidden',
  },
})
export class PlanUpgradeAwaitingPaymentComponent {
  public readonly invoice: InputSignal<InvoiceDto> = input.required<InvoiceDto>();

  readonly matDialogRef = inject(MatDialogRef);
  readonly authService: AuthService = inject(AuthService);
  readonly projectSettingsService: ProjectSettingsService = inject(ProjectSettingsService);
  readonly _apiService: ApiService = inject(ApiService);

  readonly network: WritableSignal<'TRC-20' | 'ERC-20'> = signal('TRC-20');
  public readonly QRCodeUrl: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
  public readonly isGenerating: WritableSignal<boolean> = signal<boolean>(true);

  readonly priceRange = computed(() => {
    const plan = this.invoice()?.userPlan;
    if (!plan) return '0.00';
    return getPriceRange(groupCostProviders(plan));
  });

  readonly balance: Signal<number> = computed(() => {
    const account = this.authService.accountDto();
    if (!account) return 0;
    return account.balance ?? 0;
  });

  minBalance = computed(() => this.projectSettingsService.settings()?.minBalance ?? 0);

  constructor() {
    this._generateQRCode();
  }

  private _generateQRCode(): void {
    effect(async () => {
      const invoice = this.invoice();
      if (!invoice) return;
      try {
        this.isGenerating.set(false);
        this.QRCodeUrl.set(await QRCode.toDataURL(invoice.depositAddress, { errorCorrectionLevel: 'high', margin: 0 }));
      } catch (err) {
        console.error(err);
      }
    });
  }
}
